import { useState, useEffect } from "react";
import { NAV_LINKS, LOGO_IMAGE } from "../data/mock";
import { Button } from "./ui/button";
import { Menu, X, Blocks, GraduationCap, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const Header = ({ onOrderClick, onSupportClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginChooserOpen, setLoginChooserOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close login chooser with ESC
  useEffect(() => {
    if (!loginChooserOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setLoginChooserOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loginChooserOpen]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl shadow-lg shadow-blue-900/5 border-b border-blue-100/50"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 group" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
            <img src={LOGO_IMAGE} alt="Agent ŠVP" className="h-10 w-auto" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  if (link.isPage) {
                    navigate(link.href);
                    window.scrollTo(0, 0);
                  } else {
                    handleNavClick(e, link.href);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-[#3D4F6F] hover:text-[#0049B3] transition-colors duration-300 rounded-lg hover:bg-blue-50/60"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              data-testid="login-chooser-open-desktop"
              onClick={() => setLoginChooserOpen(true)}
              className="text-sm font-medium text-[#3D4F6F] hover:text-[#0049B3] transition-colors duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Přihlášení
            </button>
            <Button
              className="bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl font-bold text-sm px-6 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40 transition-all duration-300"
              onClick={() => navigate('/podpora')}
            >
              Podpora
            </Button>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold text-sm px-6 shadow-lg shadow-emerald-400/30 hover:shadow-emerald-400/50 transition-all duration-300"
              onClick={(e) => handleNavClick(e, '#webinar')}
            >
              Bezplatná živá ukázka
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-xl hover:bg-blue-50 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6 text-[#0F1B3D]" /> : <Menu className="w-6 h-6 text-[#0F1B3D]" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          mobileOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white/95 backdrop-blur-xl border-t border-blue-100/50 px-4 py-4 space-y-1">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                setMobileOpen(false);
                if (link.isPage) {
                  navigate(link.href);
                  window.scrollTo(0, 0);
                } else {
                  handleNavClick(e, link.href);
                }
              }}
              className="block px-4 py-3 text-sm font-medium text-[#3D4F6F] hover:text-[#0049B3] hover:bg-blue-50/60 rounded-xl transition-colors"
            >
              {link.label}
            </a>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            <Button
              className="w-full bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl font-bold"
              onClick={() => { setMobileOpen(false); navigate('/podpora'); }}
            >
              Podpora
            </Button>
            <Button
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold"
              onClick={(e) => { setMobileOpen(false); handleNavClick(e, '#webinar'); }}
            >
              Bezplatná živá ukázka
            </Button>
            <button data-testid="login-chooser-open-mobile" onClick={() => { setMobileOpen(false); setLoginChooserOpen(true); }} className="block w-full text-center text-sm font-medium text-[#3D4F6F] hover:text-[#0049B3] py-3 transition-colors">
              Přihlášení pro zákazníky
            </button>
          </div>
        </div>
      </div>

      {/* Login chooser modal (rozcestník MŠ / ZŠ) */}
      {loginChooserOpen && (
        <div
          data-testid="login-chooser-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F1B3D]/50 backdrop-blur-sm"
          onClick={() => setLoginChooserOpen(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-8 pt-8 pb-5 text-center">
              <button
                data-testid="login-chooser-close"
                onClick={() => setLoginChooserOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-xl hover:bg-[#F4F7FC] transition-colors"
                aria-label="Zavřít"
              >
                <X className="w-5 h-5 text-[#6B7A99]" />
              </button>
              <h3 className="text-xl sm:text-2xl font-extrabold text-[#0F1B3D]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Kam se chcete přihlásit?
              </h3>
              <p className="text-sm text-[#6B7A99] mt-2">Vyberte aplikaci podle typu vaší školy</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 px-8 pb-8">
              <a
                href="https://app.agentsvp.cz/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="login-option-ms"
                className="group rounded-2xl border-2 border-[#E8EDF5] hover:border-[#0049B3] p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-600/10"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#0049B3]/10 flex items-center justify-center mb-4 group-hover:bg-[#0049B3] transition-colors duration-300">
                  <Blocks className="w-6 h-6 text-[#0049B3] group-hover:text-white transition-colors duration-300" />
                </div>
                <h4 className="font-extrabold text-[#0F1B3D] text-base" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Mateřská škola</h4>
                <p className="text-xs text-[#6B7A99] mt-1 mb-3">Agent ŠVP pro MŠ</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0049B3]">
                  app.agentsvp.cz
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </a>
              <a
                href="https://appzv.agentsvp.cz/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="login-option-zs"
                className="group rounded-2xl border-2 border-[#E8EDF5] hover:border-[#FFC72C] p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-400/20"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#FFC72C]/15 flex items-center justify-center mb-4 group-hover:bg-[#FFC72C] transition-colors duration-300">
                  <GraduationCap className="w-6 h-6 text-[#B78900] group-hover:text-[#0F1B3D] transition-colors duration-300" />
                </div>
                <h4 className="font-extrabold text-[#0F1B3D] text-base" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Základní škola</h4>
                <p className="text-xs text-[#6B7A99] mt-1 mb-3">Agent ŠVP pro ZŠ</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#B78900]">
                  appzv.agentsvp.cz
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
